// frontend/src/config/api/endpoints/aiEndpoints.ts
/**
 * Pensado por: Alcélio Gomes
 * Codificado em coautoria: Claude Code
 * 🚀 NeuroAgentes
 * 
 * AUDITORIA: Endpoints de AI Engine, assistente e serviços relacionados.
 */

export const aiEngineEndpoints = {
  sentiment: '/api/ai-engine/sentiment',
  intent: '/api/ai-engine/intent',
  entities: '/api/ai-engine/entities',
  analyzeText: '/api/ai-engine/analyze-text',
  analyzeComplete: '/api/ai-engine/analyze-complete',
  confidence: {
    calculate: '/api/ai-engine/confidence/calculate',
    shouldEscalate: '/api/ai-engine/confidence/should-escalate',
    health: '/api/ai-engine/confidence/health',
  },
  learning: {
    analyzeInteraction: '/api/ai-engine/learning/analyze-interaction',
    insights: (agentId: string, projectId: string) => `/api/ai-engine/learning/insights/${agentId}/${projectId}`,
    report: '/api/ai-engine/learning/report',
    modelUpdateRequests: '/api/ai-engine/learning/model-update-requests',
    requestModelUpdate: '/api/ai-engine/learning/model-update-request',
    health: '/api/ai-engine/learning/health',
  },
  agent: {
    ask: '/api/ai-engine/agent/ask',
    info: '/api/ai-engine/agent/info',
  },
  health: '/api/ai-engine/health',
  stats: '/api/ai-engine/stats',
};

export const assistantEndpoints = {
  chat: '/api/assistant/chat',
  suggestions: '/api/assistant/suggestions',
  onboardingStatus: '/api/assistant/onboarding-status',
  feedback: '/api/assistant/feedback',
  commonQuestions: '/api/assistant/common-questions',
};

export const escalationConfigEndpoints = {
  getConfiguration: (scope: string, targetId: string) => `/api/escalation-config/${scope}/${targetId}`,
  updateConfiguration: (scope: string, targetId: string) => `/api/escalation-config/${scope}/${targetId}`,
  checkPermission: '/api/escalation-config/check',
  quickToggle: '/api/escalation-config/quick-toggle',
  report: '/api/escalation-config/report',
  health: '/api/escalation-config/health',
};

export const escalationPredictionEndpoints = {
  predict: (conversationId: string) => `/api/escalation-prediction/predict/${conversationId}`,
  feedback: '/api/escalation-prediction/feedback',
  history: (conversationId: string) => `/api/escalation-prediction/history/${conversationId}`,
  stats: '/api/escalation-prediction/stats',
  health: '/api/escalation-prediction/health',
  test: '/api/escalation-prediction/test',
};

export const dynamicConfidenceEndpoints = {
  calculate: (conversationId: string) => `/api/dynamic-confidence/calculate/${conversationId}`,
  feedback: '/api/dynamic-confidence/feedback',
  getThresholds: (agentId: string) => `/api/dynamic-confidence/thresholds/${agentId}`,
  updateThresholds: (agentId: string) => `/api/dynamic-confidence/thresholds/${agentId}`,
  statistics: '/api/dynamic-confidence/statistics',
  health: '/api/dynamic-confidence/health',
};

export const patternRecognitionEndpoints = {
  analyze: (conversationId: string) => `/api/pattern-recognition/analyze/${conversationId}`,
  feedback: '/api/pattern-recognition/feedback',
  getPatterns: '/api/pattern-recognition/patterns',
  report: '/api/pattern-recognition/report',
  updateConfig: '/api/pattern-recognition/config',
  health: '/api/pattern-recognition/health',
};

export const operatorMatchingEndpoints = {
  findBestMatch: '/api/operator-matching/find-best',
  feedback: '/api/operator-matching/feedback',
  getOperators: '/api/operator-matching/operators',
  updateOperator: (operatorId: string) => `/api/operator-matching/operator/${operatorId}`,
  workload: '/api/operator-matching/workload',
  performanceReport: '/api/operator-matching/performance-report',
  health: '/api/operator-matching/health',
};

export const llmProvidersEndpoints = {
  getProviders: '/api/admin/llm-providers',
  getProviderModels: (provider: string) => `/api/admin/llm-providers/${provider}/models`,
  getUsageStats: (projectId?: string) => '/api/admin/llm-providers/usage' + (projectId ? `/${projectId}` : ''),
  updateApiKey: (provider: string) => `/api/admin/llm-providers/${provider}/api-key`,
  createProvider: '/api/admin/llm-providers',
  migrateProviders: '/api/admin/llm-providers/migrate',
  testProvider: (provider: string) => `/api/admin/llm-providers/${provider}/test`,
  getConfig: (provider: string) => `/api/llm-providers/${provider}/config`,
  getDefault: '/api/llm-providers/default',
  getActive: '/api/llm-providers/active',
  invalidateCache: '/api/llm-providers/invalidate-cache',
  cacheStatus: '/api/llm-providers/cache-status',
};

export const llmProviderConfigEndpoints = {
  getAllConfigurations: '/api/admin/llm-provider-configs',
  getConfiguration: (id: string) => `/api/admin/llm-provider-configs/${id}`,
  createConfiguration: '/api/admin/llm-provider-configs',
  updateConfiguration: (id: string) => `/api/admin/llm-provider-configs/${id}`,
  deleteConfiguration: (id: string) => `/api/admin/llm-provider-configs/${id}`,
  testConfiguration: (id: string) => `/api/admin/llm-provider-configs/${id}/test`,
  health: '/api/admin/llm-provider-configs/health',
};

export const topicClassificationEndpoints = {
  classify: '/api/topics/classify',
  analyzeBatch: '/api/topics/analyze-batch',
  getStats: '/api/topics/stats',
  health: '/api/topics/stats',
};

export const knowledgeEndpoints = {
  search: (agentId: string) => `/api/knowledge/agents/${agentId}/search`,
  stats: (agentId: string) => `/api/knowledge/agents/${agentId}/stats`,
  documents: (agentId: string) => `/api/knowledge/agents/${agentId}/documents`,
  document: (documentId: string) => `/api/knowledge/documents/${documentId}`,
  documentStatus: (documentId: string) => `/api/knowledge/documents/${documentId}/status`,
  ragConfig: (agentId: string) => `/api/knowledge/agents/${agentId}/rag-config`,
  metrics: (agentId: string) => `/api/knowledge/agents/${agentId}/metrics`,
  importUrl: (agentId: string) => `/api/knowledge/agents/${agentId}/import-url`,
  importStatus: (agentId: string, jobId: string) => `/api/knowledge/agents/${agentId}/import-status/${jobId}`,
};

export const personalityEndpoints = {
  options: '/api/personality/options',
  analyze: '/api/personality/analyze',
  health: '/api/personality/health',
  getTom: (codigo: string) => `/api/personality/ton/${codigo}`,
  getEstilo: (codigo: string) => `/api/personality/estilo/${codigo}`,
  feedback: '/api/personality/feedback',
  metrics: '/api/personality/metrics',
  dashboard: '/api/personality/dashboard',
  scientificReport: '/api/personality/scientific-report',
};
